import { ReactElement, useMemo } from 'react'
import { CellContext } from '@tanstack/react-table'
import { isAfter, sub } from 'date-fns'

import { Button } from '@/ui/Button'
import { Popup } from '@/ui/Popup'
import { DisruptionLogCode, FilterType, Log, LogSource, RouteSelectionMode } from '@/types/logs'
import { ModalTypes, useModal } from '@/components/Modal'
import { useDeleteOfficialReport } from '@/modules/OfficialReport/api/useDeleteOfficialReport'
import { useDeleteMechanicalInformation } from '@/modules/MechanicalInformation/api/useDeleteMechanicalInformation'
import { useGetUserInfo } from '@/modules/Auth'
import { useAuthStore } from '@/store/auth-store'
import { OfficialReportStatus } from '@/types/officialReport'
import { useCheckPermissions } from '@/permissions/useCheckPermissions'
import { useCreateMileageLoss } from '../../api/useCreateMileageLoss'
import { useDeleteControllerLog } from '../../api/useDeleteControllerLog'
import { getOfficialReportTypeByCode } from '../../helpers/getOfficialReportTypeByCode'
import { checkLockPermissions } from '../../helpers/checkLockPermissions'
import { checkIsLogAvailableToBeDuplicate } from '../../helpers/checkIsLogAvailableToBeDuplicate'
import { useDuplicateControllerLog } from '../../api/useDuplicateControllerLog'
import { useLockControllerLog } from '../../api/useLockControllerLog'
import { useCreateBulkCurtailment } from '../../hooks/useCreateBulkCurtailment'
import { useGetLogPermissions } from '../../api/useGetLogPermissions'
import { checkIsCreateMileageLossAvailable } from '../../helpers/checkIsCreateMileageLossAvailable'
import { useShareLog } from '../../hooks/useShareLog'
import styles from './Actions.module.scss'

import DotsIcon from '../../assets/dots.svg'
import PlusCircleIcon from '../../assets/plus-circle.svg'
import MinusCircleIcon from '../../assets/minus-circle.svg'
import DuplicateIcon from '../../assets/duplicate.svg'
import ShareIcon from '../../assets/share.svg'
import DeleteIcon from '../../assets/delete.svg'
import LockIcon from '../../assets/lock.svg'
import UnlockIcon from '../../assets/unlock.svg'
import ExpandIcon from '../../assets/expand.svg'

interface Action {
    label: string
    icon: ReactElement
    onClick: () => void
}

export const Actions = ({ row }: Pick<CellContext<Log, unknown>, 'row'>) => {
    const { role } = useAuthStore()
    const { openModal } = useModal()
    const { handleDeleteControllerLog } = useDeleteControllerLog(row.original)
    const { handleDeleteReport } = useDeleteOfficialReport({ log: row.original })
    const { handleDeleteMechanicalInformation } = useDeleteMechanicalInformation({ log: row.original })
    const { handleCreateMileageLoss } = useCreateMileageLoss(row.original)
    const { handleLockControllerLog } = useLockControllerLog()
    const { handleDuplicateLog } = useDuplicateControllerLog(row.original)
    const { handleCreateBulkCurtailment } = useCreateBulkCurtailment(row.original)
    const { handleShareLog } = useShareLog(row.original)

    const { data: userInfo } = useGetUserInfo()
    const { data: logPermissions } = useGetLogPermissions()
    const hasPermission = useCheckPermissions()

    const canLogBeUpdated = row.original.codingPeriodInfo.permissions.includes('Update')
    const isDisruptionLog = (Object.values(DisruptionLogCode) as string[]).includes(
        row.original.code?.toLocaleLowerCase()
    )

    const defaultLogActions = useMemo(() => {
        const result = []

        if (
            hasPermission('log', 'lock') &&
            checkLockPermissions(row.original) &&
            row.original.logType !== FilterType.DailyLog
        ) {
            result.push({
                label: row.original.isLocked ? 'Unlock record' : 'Lock record',
                icon: row.original.isLocked ? <UnlockIcon /> : <LockIcon />,
                onClick: () => {
                    // If the record is locked and we're in the Exceptions period, show confirmation dialog
                    if (row.original.isLocked && row.original.codingPeriodInfo.codingPeriod === 'Exceptions') {
                        openModal(ModalTypes.unlockRecordModal, {
                            deadlineDate: new Date(row.original.codingPeriodInfo.deadline),
                            onUnlock: () => {
                                handleLockControllerLog([
                                    {
                                        id: row.original.id,
                                        isLocked: false,
                                    },
                                ])
                            },
                        })
                    } else {
                        // Otherwise, just lock/unlock the record directly
                        handleLockControllerLog([
                            {
                                id: row.original.id,
                                isLocked: !row.original.isLocked,
                            },
                        ])
                    }
                },
            })
        }

        if (
            hasPermission('log', 'manageOfficialReport') &&
            (!row.original.codeTypeReasonInfo?.isOfficialReportRequired ||
                (row.original.report && row.original.report.status == OfficialReportStatus.Draft)) &&
            canLogBeUpdated &&
            row.original.source !== LogSource.Automatic
        ) {
            result.push({
                label: row.original.report ? 'Remove report' : 'Write report',
                icon: row.original.report ? <MinusCircleIcon /> : <PlusCircleIcon />,
                onClick: () => {
                    const reportType = getOfficialReportTypeByCode(row.original)

                    if (row.original.report) {
                        handleDeleteReport({
                            incidentDate: row.original.report.incidentDate,
                            reportType,
                        })
                        return
                    }

                    openModal(ModalTypes.officialReport, {
                        type: reportType,
                        log: row.original,
                    })
                },
            })
        }

        if (
            hasPermission('log', 'manageMechanicalInformation') &&
            !row.original.codeTypeReasonInfo?.isMechanicalInfoRequired &&
            canLogBeUpdated &&
            row.original.source !== LogSource.Automatic
        ) {
            result.push({
                label: row.original.mechanicalInfo ? 'Remove mechanical information' : 'Add mechanical information',
                icon: row.original.mechanicalInfo ? <MinusCircleIcon /> : <PlusCircleIcon />,
                onClick: () => {
                    if (row.original.mechanicalInfo) {
                        handleDeleteMechanicalInformation()
                        return
                    }

                    openModal(ModalTypes.mechanicalInformation, {
                        log: row.original,
                    })
                },
            })
        }

        if (
            hasPermission('log', 'createMileage') &&
            checkIsCreateMileageLossAvailable(row.original, role, userInfo) &&
            row.original.source !== LogSource.Automatic
        ) {
            result.push({
                label: 'Create mileage',
                icon: <PlusCircleIcon />,
                onClick: handleCreateMileageLoss,
            })
        }

        if (
            hasPermission('log', 'duplicate') &&
            role &&
            logPermissions &&
            checkIsLogAvailableToBeDuplicate(role, row.original, logPermissions) &&
            row.original.source !== LogSource.Automatic
        ) {
            result.push({
                label: 'Duplicate',
                icon: <DuplicateIcon />,
                onClick: handleDuplicateLog,
            })
        }

        if (hasPermission('log', 'share') && row.original.routeSelectionMode !== RouteSelectionMode.None) {
            result.push({
                label: 'Share',
                icon: <ShareIcon />,
                onClick: handleShareLog,
            })
        }

        if (
            hasPermission('log', 'delete') &&
            isAfter(new Date(row.original.timestamp), sub(new Date(), { hours: 24 })) &&
            row.original.source === LogSource.Manual &&
            row.original.codingPeriodInfo.permissions.includes('Delete')
        ) {
            result.push({
                label: 'Delete',
                icon: <DeleteIcon />,
                onClick: handleDeleteControllerLog,
            })
        }

        return result
    }, [row, userInfo, canLogBeUpdated])

    const disruptionLogActions = useMemo(() => {
        const result = []

        if (hasPermission('disruption', 'viewPDF') && row.original.descriptionInfo.extraInfo) {
            result.push({
                label: 'View source',
                icon: (
                    <span className={styles.expandIcon}>
                        <ExpandIcon />
                    </span>
                ),
                onClick: () => {
                    window.open(row.original.descriptionInfo.extraInfo, '__blank')
                },
            })
        }

        if (hasPermission('disruption', 'share') && row.original.routeSelectionMode !== RouteSelectionMode.None) {
            result.push({
                label: 'Share',
                icon: <ShareIcon />,
                onClick: handleShareLog,
            })
        }

        if (hasPermission('disruption', 'createBulkCurtailment') && canLogBeUpdated) {
            result.push({
                label: 'Create bulk curtailment',
                icon: <PlusCircleIcon />,
                onClick: () => {
                    handleCreateBulkCurtailment()
                },
            })
        }

        if (
            hasPermission('log', 'delete') &&
            isAfter(new Date(row.original.timestamp), sub(new Date(), { hours: 24 })) &&
            row.original.source === LogSource.Manual &&
            row.original.codingPeriodInfo.permissions.includes('Delete')
        ) {
            result.push({
                label: 'Delete',
                icon: <DeleteIcon />,
                onClick: handleDeleteControllerLog,
            })
        }

        return result
    }, [row, canLogBeUpdated])

    const actions: Action[] = useMemo(
        () => (isDisruptionLog ? disruptionLogActions : defaultLogActions),
        [isDisruptionLog, disruptionLogActions, defaultLogActions]
    )

    if (!actions.length) return null

    return (
        <Popup
            className={styles.popup}
            triggerComponent={
                <Button className={styles.trigger} size="small" variant="transparent">
                    <DotsIcon />
                </Button>
            }
            attachToTrigger
            offset={0}
            closeOnClick
            placement="bottom-end"
        >
            <div className={styles.actions}>
                {actions.map(({ label, icon, onClick }, index) => (
                    <Button key={index} className={styles.action} variant="transparent" size="small" onClick={onClick}>
                        <span className={styles.actionIcon}>{icon}</span>
                        <span>{label}</span>
                    </Button>
                ))}
            </div>
        </Popup>
    )
}
